import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, ExternalLink, Loader2, ArrowLeft, Search, Cloud, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  listCloudTemplates,
  detectTemplatePlaceholders,
  mergeCloudTemplate,
  CloudTemplateError,
  type CloudProvider,
  type CloudTemplate,
} from "@/lib/cloudTemplates";
import { supabase } from "@/integrations/supabase/client";

/** Human labels for the failing pipeline step (matches edge function step ids). */
const STEP_LABELS: Record<string, string> = {
  ms_token_refresh: "Rafraîchissement du token Microsoft",
  ms_graph_download_template: "Téléchargement du modèle Word (Microsoft Graph)",
  ms_graph_upload_merged: "Envoi du document fusionné vers OneDrive",
  word_parse_template: "Analyse du modèle Word (.docx)",
  word_merge: "Fusion du modèle Word",
  google_token_refresh: "Rafraîchissement du token Google",
  google_docs_fetch_template: "Lecture du modèle Google Docs",
  google_drive_copy_template: "Copie du modèle dans Google Drive",
  google_docs_batch_update: "Fusion Google Docs (batchUpdate)",
  ms_refresh_token: "Rafraîchissement du token Microsoft",
  ms_graph_search_docx: "Recherche des modèles .docx dans OneDrive",
  google_refresh_token: "Rafraîchissement du token Google",
  google_drive_list: "Listing des documents Google Drive",
  client_timeout: "Délai dépassé côté client (serveur trop lent)",
  no_weburl: "Réponse du serveur incomplète",
  unknown: "Étape inconnue",
};


/** Default image URLs auto-filled into image placeholders (logo, signature, …). */
export type ImageDefaults = Record<string, string>;

const URL_RE = /^https?:\/\//i;
const pickUrl = (v: unknown): string | null =>
  typeof v === "string" && URL_RE.test(v.trim()) ? v.trim() : null;

let _imgCache: { value: ImageDefaults; ts: number } | null = null;

/** Fetch image defaults from owner_branding + profiles, cached 5 min. */
async function fetchImageDefaults(): Promise<ImageDefaults> {
  if (_imgCache && Date.now() - _imgCache.ts < 5 * 60_000) return _imgCache.value;
  const out: ImageDefaults = {};
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return out;

    // 1. owner_branding (as owner, else via owner_coaches link)
    let logoUrl: string | null = null;
    const { data: own } = await supabase
      .from("owner_branding")
      .select("logo_url")
      .eq("owner_id", user.id)
      .maybeSingle();
    logoUrl = pickUrl(own?.logo_url);
    if (!logoUrl) {
      const { data: link } = await supabase
        .from("owner_coaches")
        .select("owner_id")
        .eq("coach_id", user.id)
        .limit(1)
        .maybeSingle();
      if (link?.owner_id) {
        const { data } = await supabase
          .from("owner_branding")
          .select("logo_url")
          .eq("owner_id", link.owner_id)
          .maybeSingle();
        logoUrl = pickUrl(data?.logo_url);
      }
    }

    // 2. profile fallbacks for logo + signature/avatar
    const { data: prof } = await supabase
      .from("profiles")
      .select("company_logo_url, avatar_url, email_signature")
      .eq("id", user.id)
      .maybeSingle();

    const logo = logoUrl || pickUrl((prof as any)?.company_logo_url) || pickUrl((prof as any)?.avatar_url);
    if (logo) out.logo = logo;

    // email_signature may store HTML or a plain URL — only use it if it's a URL
    const sig = pickUrl((prof as any)?.email_signature);
    if (sig) out.signature = sig;

    const avatar = pickUrl((prof as any)?.avatar_url);
    if (avatar) {
      if (!out.signature) out.signature = avatar; // last-resort signature image
      out.avatar = avatar;
      if (!out.photo) out.photo = avatar;
    }
  } catch {
    // ignore — best-effort prefill
  }
  _imgCache = { value: out, ts: Date.now() };
  return out;
}

/** Image-like placeholder keys we'll try to auto-fill from the user's branding. */
const IMAGE_KEYS = new Set([
  "logo", "signature", "avatar", "photo",
  "company_logo", "header_image", "cover_image",
]);
const isImageKey = (k: string) => IMAGE_KEYS.has(k.toLowerCase()) || /(_|^)(logo|image|photo|signature|avatar)$/i.test(k);

export type CloudTemplateExportResult =
  | { ok: true; webUrl?: string }
  | { ok: false; cancelled: true }
  | null;

type Resolver = (v: CloudTemplateExportResult) => void;

interface OpenPayload {
  defaultProvider?: CloudProvider;
  defaultValues: Record<string, string>;
  contentMarkdown: string;
  filename: string;
  resolve: Resolver;
}

const EVENT_NAME = "thiia:cloud-template-export:open";

export interface CloudTemplateExportRequest {
  /** Pre-select a provider if known (otherwise the user picks). */
  defaultProvider?: CloudProvider;
  /** Pre-filled values for placeholders (title, client, date, author, company, prospect, event, signature). */
  defaultValues: Record<string, string>;
  /** Markdown content to inject in the body. Empty string skips body injection. */
  contentMarkdown: string;
  /** Suggested filename (also document title for Google). */
  filename: string;
}

/**
 * Opens the cloud template export flow:
 *   1. Pick provider (Word Online / Google Docs)
 *   2. Pick template from the user's cloud account
 *   3. Fill detected placeholders
 *   4. Merge and open in the cloud editor
 */
export function openCloudTemplateExport(
  req: CloudTemplateExportRequest,
): Promise<CloudTemplateExportResult> {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent<OpenPayload>(EVENT_NAME, {
        detail: {
          defaultProvider: req.defaultProvider,
          defaultValues: req.defaultValues,
          contentMarkdown: req.contentMarkdown,
          filename: req.filename,
          resolve,
        },
      }),
    );
  });
}

const FIELD_LABELS: Record<string, string> = {
  title: "Titre",
  client: "Client",
  date: "Date",
  author: "Auteur",
  company: "Société",
  prospect: "Prospect",
  event: "Événement",
  signature: "Signature",
  logo: "Logo",
  content: "Contenu",
};

type Step = "provider" | "list" | "fields" | "running";

export default function CloudTemplateExportHost() {
  const [state, setState] = useState<OpenPayload | null>(null);
  const [step, setStep] = useState<Step>("provider");
  const [provider, setProvider] = useState<CloudProvider | null>(null);
  const [templates, setTemplates] = useState<CloudTemplate[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<{ message: string; step?: string; detail?: string } | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CloudTemplate | null>(null);
  const [fields, setFields] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [merging, setMerging] = useState(false);
  const [lastError, setLastError] = useState<{ message: string; step?: string; detail?: string } | null>(null);


  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenPayload>).detail;
      if (!detail) return;
      setState(detail);
      setStep(detail.defaultProvider ? "list" : "provider");
      setProvider(detail.defaultProvider ?? null);
      setTemplates([]);
      setListError(null);
      setQuery("");
      setSelected(null);
      setFields([]);
      setValues({});
      setMerging(false);
      setLastError(null);
    };

    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  // Load templates when provider is set & step is list
  useEffect(() => {
    if (!state || step !== "list" || !provider) return;
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      setListError(null);
      try {
        const list = await listCloudTemplates(provider);
        if (!cancelled) setTemplates(list);
      } catch (e: any) {
        if (!cancelled) setListError({ message: e?.message || "Erreur de chargement", step: e?.step, detail: e?.detail });
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [state, step, provider]);

  const close = (result: CloudTemplateExportResult) => {
    state?.resolve(result);
    setState(null);
  };

  const pickProvider = (p: CloudProvider) => {
    setProvider(p);
    setStep("list");
  };

  const pickTemplate = async (tpl: CloudTemplate) => {
    if (!state || !provider) return;
    setSelected(tpl);
    setStep("running");
    setLastError(null);
    try {
      const detected = await Promise.race<string[]>([
        detectTemplatePlaceholders(provider, tpl.id),
        new Promise<string[]>((_, rej) =>
          setTimeout(() => {
            const err = new CloudTemplateError(
              "Le modèle met trop de temps à répondre (>45s). Réessayez ou choisissez un autre modèle.",
              "client_timeout",
            );
            rej(err);
          }, 45_000),
        ),
      ]);
      const orderedKnown = ["title", "client", "date", "author", "company", "prospect", "event", "signature", "logo"];
      const ordered = [
        ...orderedKnown.filter((k) => detected.includes(k)),
        ...detected.filter((k) => !orderedKnown.includes(k) && k !== "content"),
      ];
      const v: Record<string, string> = {};
      for (const f of ordered) v[f] = state.defaultValues[f] ?? "";
      const imageFields = ordered.filter((f) => !v[f] && isImageKey(f));
      if (imageFields.length > 0) {
        const defaults = await fetchImageDefaults();
        for (const f of imageFields) {
          const url = defaults[f] || defaults[f.toLowerCase()] || (f.toLowerCase().includes("logo") ? defaults.logo : "");
          if (url) v[f] = url;
        }
      }
      setFields(ordered);
      setValues(v);
      // If no textual fields, merge directly. IMPORTANT: pass `tpl` explicitly —
      // `selected` state is not yet committed when this runs.
      if (ordered.length === 0) {
        await runMerge(v, tpl);
        return;
      }
      setStep("fields");
    } catch (e: any) {
      const step = e instanceof CloudTemplateError ? e.step : undefined;
      const detail = e instanceof CloudTemplateError ? e.detail : undefined;
      setLastError({ message: e?.message || "Erreur inconnue", step, detail });
      toast.error("Détection impossible", {
        description: step ? `${STEP_LABELS[step] || step} — ${e?.message}` : e?.message,
      });
      setStep("list");
    }
  };


  const runMerge = async (v: Record<string, string>, tplOverride?: CloudTemplate) => {
    const tpl = tplOverride ?? selected;
    if (!state || !provider || !tpl) return;
    setMerging(true);
    setStep("running");
    setLastError(null);
    try {
      const { webUrl } = await mergeCloudTemplate({
        provider,
        templateId: tpl.id,
        values: v,
        contentMarkdown: state.contentMarkdown || null,
        filename: state.filename,
      });
      toast.success(provider === "word" ? "Ouverture dans Word Online" : "Ouverture dans Google Docs");
      window.open(webUrl, "_blank", "noopener,noreferrer");
      close({ ok: true, webUrl });
    } catch (e: any) {
      const step = e instanceof CloudTemplateError ? e.step : undefined;
      const detail = e instanceof CloudTemplateError ? e.detail : undefined;
      setLastError({ message: e?.message || "Erreur inconnue", step, detail });
      toast.error("Fusion impossible", {
        description: step ? `${STEP_LABELS[step] || step} — ${e?.message}` : e?.message,
      });
      setStep("fields");
    } finally {
      setMerging(false);
    }
  };


  const filteredTemplates = query.trim()
    ? templates.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()))
    : templates;

  if (!state) return null;

  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) close({ ok: false, cancelled: true }); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Cloud className="w-4 h-4" />
            Exporter vers un modèle cloud
          </DialogTitle>
          <DialogDescription>
            {step === "provider" && "Choisissez où récupérer le modèle."}
            {step === "list" && `Choisissez un modèle ${provider === "word" ? "Word Online" : "Google Docs"} parmi votre librairie.`}
            {step === "fields" && "Renseignez les champs détectés dans le modèle. Le contenu de l'export sera inséré en plus."}
            {step === "running" && "Préparation du document…"}
          </DialogDescription>
        </DialogHeader>

        {lastError && (step === "list" || step === "fields") && (
          <div className="flex items-start gap-2 p-3 rounded-sm border border-destructive/40 bg-destructive/5 text-xs">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-destructive">
                Étape bloquée : {STEP_LABELS[lastError.step || "unknown"] || lastError.step || "Étape inconnue"}
              </div>
              <div className="mt-0.5 text-foreground/80 break-words">{lastError.message}</div>
              {lastError.detail && (
                <pre className="mt-1.5 max-h-24 overflow-auto whitespace-pre-wrap text-[13px] text-muted-foreground bg-background/50 rounded-sm p-1.5 border">
                  {lastError.detail}
                </pre>
              )}
            </div>
          </div>
        )}


        {step === "provider" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <button
              onClick={() => pickProvider("word")}
              className="border rounded-lg p-4 text-left hover:bg-accent transition flex flex-col gap-2"
            >
              <div className="font-medium text-sm">Word Online</div>
              <div className="text-xs text-muted-foreground">
                Modèles .docx depuis votre OneDrive
              </div>
            </button>
            <button
              onClick={() => pickProvider("google")}
              className="border rounded-lg p-4 text-left hover:bg-accent transition flex flex-col gap-2"
            >
              <div className="font-medium text-sm">Google Docs</div>
              <div className="text-xs text-muted-foreground">
                Documents Google Docs depuis votre Drive
              </div>
            </button>
          </div>
        )}

        {step === "list" && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Rechercher un modèle…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 pl-8 text-sm"
              />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1">
              {loadingList && (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
                </div>
              )}
              {!loadingList && listError && (
                <div className="flex items-start gap-2 p-3 rounded-sm border border-destructive/40 bg-destructive/5 text-xs">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-destructive">
                      Étape bloquée : {STEP_LABELS[listError.step || "unknown"] || listError.step || "Chargement de la liste"}
                    </div>
                    <div className="mt-0.5 text-foreground/80 break-words">{listError.message}</div>
                    {listError.detail && (
                      <pre className="mt-1.5 max-h-24 overflow-auto whitespace-pre-wrap text-[13px] text-muted-foreground bg-background/50 rounded-sm p-1.5 border">
                        {listError.detail}
                      </pre>
                    )}
                  </div>
                </div>
              )}
              {!loadingList && !listError && filteredTemplates.length === 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Aucun modèle trouvé.
                </div>
              )}
              {!loadingList && filteredTemplates.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => pickTemplate(tpl)}
                  className="w-full flex items-center gap-2 p-2 rounded-sm border hover:bg-accent transition text-left"
                >
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{tpl.name}</div>
                    {tpl.modified_at && (
                      <div className="text-[13px] text-muted-foreground">
                        Modifié le {format(new Date(tpl.modified_at), "d MMM yyyy", { locale: fr })}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="text-[13px] text-muted-foreground pt-2 border-t mt-2">
              Astuce : ajoutez des balises <span className="font-mono">{"{{title}}"}</span>, <span className="font-mono">{"{{client}}"}</span>, <span className="font-mono">{"{{content}}"}</span>… dans vos modèles pour qu'elles soient remplacées automatiquement.
            </div>
          </div>
        )}

        {step === "fields" && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
              {fields.map((fk) => {
                const isImage = fk === "logo" || fk === "signature";
                return (
                  <div key={fk} className="space-y-1">
                    <Label className="text-xs font-medium">
                      {FIELD_LABELS[fk] || fk}
                      <span className="font-mono text-xs text-muted-foreground ml-2">{"{{" + fk + "}}"}</span>
                      {isImage && (
                        <span className="text-[13px] text-muted-foreground ml-2">(URL d'image https://…)</span>
                      )}
                    </Label>
                    <Input
                      value={values[fk] || ""}
                      onChange={(e) => setValues({ ...values, [fk]: e.target.value })}
                      placeholder={isImage ? "https://exemple.com/logo.png" : undefined}
                      className="h-9 text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === "running" && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              {merging ? "Fusion du modèle en cours…" : "Analyse du modèle…"}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          {step === "list" && !state.defaultProvider && (
            <Button variant="ghost" size="sm" onClick={() => setStep("provider")}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Retour
            </Button>
          )}
          {step === "fields" && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep("list")}>
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Changer de modèle
              </Button>
              <Button size="sm" onClick={() => runMerge(values)} disabled={merging}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Ouvrir le document
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => close({ ok: false, cancelled: true })}>
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
